import { Big } from 'big.js';
import BreJob from '~/tenants/bre/model/BreJob';
import BreAerialConfig, {
  bre_is_aerial_photo,
  bre_is_aerial_video,
  BreAerialType
} from '~/tenants/bre/performable/aerial/BreAerialConfig';
import { DisplayData, TenantJobLine } from '~/lib/model';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import { BreOrderType } from '~/tenants/bre/model/enums';
import { BRE_COPYRIGHT_PHOTO_FEE, BRE_COPYRIGHT_VIDEO_FEE, bre_version, BreVersion } from '~/tenants/bre/data';
import {
  bre_increase,
  bre_increase_commercial,
  bre_increase_residential,
  bre_round,
  bre_split_photo,
  bre_split_video
} from '~/tenants/bre/performable/common';
import BreAerialData from '~/tenants/bre/performable/aerial/BreAerialData';
import { bre_order_has_social, BreOrderContext } from '~/tenants/bre/model/BreOrderContext';
import { BreProviderId } from '~/tenants/bre/model/BreProvider';

export default class BreAerialJob extends BreJob<typeof BreAerialConfig> {
  get performable(): typeof BreAerialConfig {
    return BreAerialConfig;
  }

  get configurable(): boolean {
    // allow customer to choose points of interest
    return true;
  }

  get editing() {
    return this.uploading && bre_is_aerial_photo(this.metadata.type);
  }

  get uploading() {
    return false;
    // if (this.appointment && bre_version(this.appointment.start, BreVersion.V2)) {
    //   return true;
    // }
    //
    // return bre_version(this.order.created, BreVersion.V2);
  }

  /** The photographer's per-job "AI edit" choice, AND the company's editing switch (as BrePhotosJob). */
  get autoenhance() {
    return this.order.editing?.enabled === true && Boolean(this.metadata.ai_edit);
  }

  get submission() {
    // photographer has to declare if they are self-editing or using photo editing credits
    return true;
  }

  get name(): string {
    return this.minimumPhotos && this.video ? 'Aerial Photos & Video' : this.minimumPhotos ? 'Aerial Photos' : 'Aerial Video';
  }

  info(): DisplayData[] {
    const info = super.info();

    if (this.metadata.poi && this.metadata.poi.length > 0) {
      info.push({
        name: 'Points of Interest',
        provider: true,
        value: this.metadata.poi.map((poi) => `${poi.name} - ${poi.location}`).join(', '),
      });
    }

    if (this.minimumPhotos > 0) {
      info.push({
        name: 'Photos',
        provider: true,
        value: this.minimumPhotos.toString(),
      });
    }

    return info;
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.RESIDENTIAL, BreOrderType.COMMERCIAL, BreOrderType.RENTAL, BreOrderType.LONG_TERM);
  }

  defaultValue(): ZodVersionedMetadata<(typeof BreAerialConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      type: BreAerialType.FIVE,
    };
  }

  get editor() {
    return this.editing ? BreProviderId.NIGHT_LEO.toString() : null;
  }

  onsite(): number {
    const poi = this.metadata.poi?.length ? 20 : 0;

    return BreAerialData[this.metadata.type].onsite + poi;
  }

  revenueLines(): TenantJobLine[] {
    const lines = super.revenueLines();

    lines.push({
      id: 'aerial',
      description: BreAerialJob.calculateName(this.order, this.metadata.type),
      amount: BreAerialJob.calculateIncreasedRate(this.order, this.metadata.type),
    });

    if (this.isOneOf(BreOrderType.LONG_TERM)) {
      lines.push({
        id: 'copyright',
        description: 'Copyright',
        amount: this.calculateCopyright,
      });
    }

    if (this.metadata.poi && this.metadata.poi.length > 0) {
      lines.push({
        id: 'poi',
        description: `Points of Interest (x${this.metadata.poi.length})`,
        amount: this.calculatePoi(this.metadata.poi),
      });
    }

    return lines;
  }

  expenseLines(): TenantJobLine[] {
    const lines = super.expenseLines();

    const total = BreAerialJob.calculateOriginalRate(this.order, this.metadata.type);

    let photo = new Big(0);
    let video = new Big(0);

    if (this.metadata.type === BreAerialType.COMBO) {
      photo = total.times('0.5');
      video = total.minus(photo);
    } else if (this.minimumPhotos > 0) {
      photo = total;
    } else if (this.video) {
      video = total;
    }

    if (photo.gt(0)) {
      lines.push({
        id: 'photos',
        description: `${this.lighting} Aerial Photos (${this.minimumPhotos})`,
        amount: bre_split_photo(photo),
      });
    }

    if (video.gt(0)) {
      lines.push({
        id: 'video',
        description: `${this.lighting} Aerial Video`,
        amount: bre_split_video(video, !this.metadata.self_edited),
      });
    }

    if (this.metadata.poi && this.metadata.poi.length > 0) {
      lines.push({
        id: 'poi',
        description: `Points of Interest (${this.metadata.poi.length})`,
        amount: bre_split_photo(this.calculatePoi(this.metadata.poi)),
      });
    }

    if (this.metadata.editing_credits && this.metadata.editing_credits > 0) {
      lines.push({
        id: 'editing_credits',
        description: 'Editing Credits',
        amount: new Big(this.metadata.editing_credits).times('-1'),
      });
    }

    return lines;
  }

  private calculatePoi(poi: any[]) {
    return poi.length > 7
      ? new Big(BreAerialData.poi_each).times(Math.max(poi.length - 7, 0)).plus(BreAerialData.poi)
      : new Big(BreAerialData.poi);
  }

  private get calculateCopyright(): Big {
    const type = this.order.metadata.type;

    return bre_increase(
      type,
      new Big(BRE_COPYRIGHT_PHOTO_FEE[type]).times(this.minimumPhotos).plus(this.video ? BRE_COPYRIGHT_VIDEO_FEE[type] : '0'),
    );
  }

  get minimumPhotos(): number {
    if (this.metadata.type === BreAerialType.COMBO && bre_order_has_social(this.order)) {
      return 5;
    }

    return BreAerialData[this.metadata.type].photos;
  }

  private get video(): boolean {
    return bre_is_aerial_video(this.metadata.type);
  }

  private get lighting(): string {
    return BreAerialJob.calculateLighting(this.metadata.type);
  }

  static calculateName(context: BreOrderContext, type: BreAerialType): string {
    if (type === BreAerialType.COMBO && bre_order_has_social(context)) {
      return 'Social Aerial Upgrade';
    }

    const photos = this.calculatePhotos(context, type);

    const up = photos > 0 ? `Up to ${photos} ${this.calculateLighting(type)} Aerial Photos` : '';

    if (type === BreAerialType.VIDEO) {
      return 'Daytime Aerial Video Only';
    }

    if (type === BreAerialType.COMBO) {
      return `${up} + Video`;
    }

    return up;
  }

  static calculateOriginalRate(context: BreOrderContext, type: BreAerialType): Big {
    if (type === BreAerialType.COMBO && bre_order_has_social(context)) {
      return new Big('200');
    }

    const original = new Big(BreAerialData[type].amount);

    if (context.metadata.type === BreOrderType.COMMERCIAL) {
      return bre_increase_commercial(original);
    }

    return original;
  }

  static calculateIncreasedRate(context: BreOrderContext, type: BreAerialType): Big {
    const original = this.calculateOriginalRate(context, type);

    if (type === BreAerialType.COMBO && bre_order_has_social(context)) {
      return original;
    }

    return bre_round(bre_increase_residential(original));
  }

  static calculatePhotos(context: BreOrderContext, type: BreAerialType): number {
    if (type === BreAerialType.COMBO && bre_order_has_social(context)) {
      return 5;
    }

    return BreAerialData[type].photos;
  }

  static calculateLighting(type: BreAerialType): string {
    return type === BreAerialType.TWILIGHT ? 'Twilight' : 'Daytime';
  }
}
